import { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calculator, ArrowRightLeft, TrendingUp, ArrowLeft, Plus, X, AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const normCarrier = (c) => {
  const s = (c || '').trim();
  if (/icloud|passcode/i.test(s)) return 'iCloud Locked';
  if (s.toLowerCase() === 'unlocked') return 'Unlocked';
  if (s.toLowerCase() === 'locked') return 'Locked';
  return s;
};
const carrierEq = (a, b) => normCarrier(a) === normCarrier(b);

export default function CalculatorPage({ embedded = false }) {
  const [entries, setEntries] = useState([]);
  const [model, setModel] = useState('');
  const [carrier, setCarrier] = useState('');
  const [storage, setStorage] = useState('');
  const [grade, setGrade] = useState('');
  const [mode, setMode] = useState('profit_to_price');
  const [targetProfit, setTargetProfit] = useState('');
  const [margins, setMargins] = useState([]);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [repairCosts, setRepairCosts] = useState('0');
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const calculationVersion = useRef(0);

  // Deductions: list of { label, amount, enabled, is_ask, source }
  // - source: 'preset' (came from price sheet) or 'custom' (user added)
  const [deductionsLoaded, setDeductionsLoaded] = useState({});  // model -> items[]
  const [deductionState, setDeductionState] = useState([]);      // current rows being edited

  useEffect(() => {
    const load = async () => {
      try {
        const [r, saved] = await Promise.all([
          axios.get(`${API}/pricesheet`, { params: { include_parts_ic: true, include_all_sections: true } }),
          axios.get(`${API}/margins`),
        ]);
        setEntries(r.data.entries || []);
        setMargins(saved.data || []);
      } catch (e) {
        toast.error('Failed to load price sheet');
      }
    };
    load();
  }, []);

  // When model changes, fetch its deductions
  useEffect(() => {
    let cancelled = false;
    if (!model) {
      setDeductionState([]);
      return;
    }
    const fetchDeductions = async () => {
      try {
        if (deductionsLoaded[model]) {
          setDeductionState(presetsToState(deductionsLoaded[model]));
          return;
        }
        const r = await axios.get(`${API}/pricesheet/deductions`, { params: { model } });
        if (cancelled) return;
        const items = (r.data && Array.isArray(r.data.items)) ? r.data.items : [];
        setDeductionsLoaded(prev => ({ ...prev, [model]: items }));
        setDeductionState(presetsToState(items));
      } catch {
        if (!cancelled) setDeductionState([]);
      }
    };
    fetchDeductions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const models = useMemo(() => [...new Set(entries.map(e => e.model))], [entries]);
  const carriers = useMemo(() => {
    const seen = new Set();
    const out = [];
    entries
      .filter(e => e.model === model)
      .forEach(e => {
        const n = normCarrier(e.carrier);
        if (n && !seen.has(n)) { seen.add(n); out.push(n); }
      });
    return out;
  }, [entries, model]);
  const storages = useMemo(
    () => [...new Set(entries.filter(e => e.model === model && carrierEq(e.carrier, carrier)).map(e => e.storage))],
    [entries, model, carrier]
  );
  const grades = useMemo(
    () => [...new Set(entries.filter(
      e => e.model === model && carrierEq(e.carrier, carrier) && e.storage === storage
    ).map(e => e.grade))],
    [entries, model, carrier, storage]
  );

  const currentEntry = useMemo(() => {
    const matching = entries.filter(
      e => e.model === model && carrierEq(e.carrier, carrier) && e.storage === storage && e.grade === grade
    );
    return matching.find(e => e.sheet_source && !['used', 'parts_ic'].includes(e.sheet_source)) || matching[0] || null;
  }, [entries, model, carrier, storage, grade]);
  const currentBuyerPrice = currentEntry?.price ?? null;

  const isICloud = /icloud|passcode/i.test(carrier || '');

  useEffect(() => { setCarrier(''); setStorage(''); setGrade(''); }, [model]);
  useEffect(() => {
    setGrade('');
    // iCloud Locked prices don't vary by storage — lock it to ANY.
    setStorage(/icloud|passcode/i.test(carrier || '') ? 'ANY' : '');
  }, [carrier]);
  useEffect(() => { setGrade(''); }, [storage]);

  const savedRule = useMemo(() => {
    const source = currentEntry?.sheet_source === 'parts_ic' ? 'used' : (currentEntry?.sheet_source || 'used');
    const scoped = margins.filter(m => (m.sheet_source || 'used') === source);
    let matching = scoped.filter(m => m.model === model && carrierEq(m.carrier, carrier));
    if (!matching.length && carrier === 'ATT Locked') matching = scoped.filter(m => m.model === model && m.carrier === 'Locked');
    matching.sort((a, b) => Number(b.storage === storage) - Number(a.storage === storage)
      || Number(b.storage === 'ANY') - Number(a.storage === 'ANY')
      || (b.updated_at || '').localeCompare(a.updated_at || ''));
    return matching[0] || null;
  }, [margins, model, carrier, storage, currentEntry]);
  const savedMargin = savedRule && savedRule.sheet_source && savedRule.sheet_source !== 'used'
    ? (currentBuyerPrice == null ? null : currentBuyerPrice - Math.floor((savedRule.margin_type === 'percentage'
        ? currentBuyerPrice * (1 - savedRule.profit_margin) : currentBuyerPrice - savedRule.profit_margin) / 10 + 1e-9) * 10)
    : (savedRule?.profit_margin ?? null);
  useEffect(() => { setTargetProfit(savedMargin == null ? '' : String(savedMargin)); }, [savedMargin, model, carrier, storage]);

  // Total of selected deductions (sent to backend as `adjustments`)
  const adjustmentsTotal = useMemo(() => {
    return deductionState
      .filter(d => d.enabled && d.amount !== '' && !isNaN(parseFloat(d.amount)))
      // Stored as positive number for UX; treat as negative
      .reduce((sum, d) => sum - Math.abs(parseFloat(d.amount) || 0), 0);
  }, [deductionState]);

  useEffect(() => {
    calculationVersion.current += 1;
    setResult(null);
    setCalculating(false);
  }, [model, carrier, storage, grade, mode, targetProfit, purchasePrice, repairCosts, adjustmentsTotal]);

  const effectivePrice = currentBuyerPrice == null ? null : currentBuyerPrice + adjustmentsTotal;
  const liveProfit = mode === 'profit_to_price' ? (targetProfit === '' ? null : Number(targetProfit))
    : (purchasePrice === '' || effectivePrice == null ? null : effectivePrice - Number(purchasePrice) - Number(repairCosts || 0));
  const liveMargin = effectivePrice > 0 && liveProfit != null ? (liveProfit / effectivePrice * 100).toFixed(1) : null;

  const calculate = async () => {
    if (!model || !carrier || !storage || !grade) {
      toast.error('Pick a model, carrier, storage and grade first');
      return;
    }
    const version = ++calculationVersion.current;
    setCalculating(true);
    try {
      const body = {
        sheet_source: currentEntry?.sheet_source === 'parts_ic' ? 'used' : (currentEntry?.sheet_source || 'used'),
        model, carrier, storage, grade, mode,
        repair_costs: parseFloat(repairCosts) || 0,
        adjustments: adjustmentsTotal,
      };
      if (mode === 'profit_to_price') body.target_profit = parseFloat(targetProfit) || 0;
      else body.purchase_price = parseFloat(purchasePrice) || 0;
      const r = await axios.post(`${API}/pricesheet/calculate`, body);
      if (version === calculationVersion.current) setResult(r.data);
    } catch (e) {
      if (version === calculationVersion.current) {
        toast.error(e.response?.data?.detail || 'Calculation failed');
        setResult(null);
      }
    } finally {
      if (version === calculationVersion.current) setCalculating(false);
    }
  };

  const toggleDeduction = (idx, enabled) => {
    setDeductionState(prev => prev.map((d, i) => i === idx ? { ...d, enabled } : d));
  };

  const setDeductionAmount = (idx, amount) => {
    // Strip negative sign — the dropdown is for deductions, sign is implicit
    const cleaned = (amount || '').replace(/^-+/, '');
    setDeductionState(prev => prev.map((d, i) =>
      i === idx ? { ...d, amount: cleaned, enabled: cleaned !== '' && !isNaN(parseFloat(cleaned)) } : d
    ));
  };

  const addCustomDeduction = () => {
    setDeductionState(prev => [
      ...prev,
      {
        // Stable random id so React keeps focus when the user types a label
        __rid: `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: '', amount: '', enabled: true, is_ask: false, source: 'custom',
      },
    ]);
  };

  const updateCustomLabel = (idx, label) => {
    setDeductionState(prev => prev.map((d, i) => i === idx ? { ...d, label } : d));
  };

  const removeDeduction = (idx) => {
    setDeductionState(prev => prev.filter((_, i) => i !== idx));
  };

  const allSelected = model && carrier && storage && grade;

  return (
    <div className={embedded ? 'space-y-3 [&_label]:text-xs [&_input]:h-9 [&_[role=combobox]]:h-9' : 'space-y-6 max-w-5xl'} data-testid="calculator-page">
      {!embedded && <div className="flex items-center gap-3">
        <Link to="/pricesheet">
          <Button variant="ghost" size="sm" data-testid="back-to-pricesheet">
            <ArrowLeft className="h-4 w-4 mr-1" /> Price Sheet
          </Button>
        </Link>
      </div>}

      {!embedded && <div>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide flex items-center gap-3">
          <Calculator className="h-8 w-8 text-primary" /> PROFIT CALCULATOR
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Decide how much to pay — or what profit you'll make — using your buyer's current prices and deductions.
        </p>
      </div>}

      {entries.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No prices in your sheet yet.</p>
          <Link to="/pricesheet">
            <Button>Add buyer prices first</Button>
          </Link>
        </Card>
      ) : (
        <div className={embedded ? 'flex flex-col gap-3' : 'grid lg:grid-cols-2 gap-6'}>
          <Card className={embedded ? 'border-0 shadow-none p-0 space-y-3' : 'p-6 space-y-4'} data-testid="calc-inputs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Model</label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="select-model"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    {models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Carrier</label>
                <Select value={carrier} onValueChange={setCarrier} disabled={!model}>
                  <SelectTrigger data-testid="select-carrier"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    {carriers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Storage</label>
                <Select value={storage} onValueChange={setStorage} disabled={!carrier}>
                  <SelectTrigger data-testid="select-storage"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    {storages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Grade</label>
                <Select value={grade} onValueChange={setGrade} disabled={!storage}>
                  <SelectTrigger data-testid="select-grade"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border border-sheet/20 bg-sheet-soft/30 p-3 space-y-1 text-sm" data-testid="calculator-margin-summary">
              <p data-testid="calculator-saved-margin">Saved margin: {savedRule?.margin_type === 'percentage' ? `${savedRule.profit_margin * 100}% · pay rounded down to $10` : savedMargin == null ? 'Not set — enter a target profit below' : `$${Number(savedRule?.profit_margin ?? savedMargin).toLocaleString()}${savedRule?.sheet_source && savedRule.sheet_source !== 'used' ? ' · pay rounded down to $10' : ''}`}</p>
              <p data-testid="calculator-live-profit">{mode === 'profit_to_price' ? 'Target' : 'Projected'} profit: {liveProfit == null ? '—' : `$${liveProfit.toLocaleString()}`}</p>
              <p data-testid="calculator-live-margin">Profit margin: {liveMargin == null ? '—' : `${liveMargin}% of selling price`}</p>
            </div>
            {currentBuyerPrice !== null && (
              <div className="rounded-lg bg-accent/40 px-4 py-3 border border-accent">
                <p className="text-xs text-accent-foreground/80 uppercase tracking-wide">Buyer Pays</p>
                <p className="text-2xl font-bold text-accent-foreground" data-testid="current-buyer-price">
                  ${currentBuyerPrice.toLocaleString()}
                </p>
              </div>
            )}

            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm font-medium text-foreground">Calculation Mode</span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setMode(m => m === 'profit_to_price' ? 'price_to_profit' : 'profit_to_price')}
                  data-testid="toggle-mode"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  {mode === 'profit_to_price' ? 'Profit → Max Price' : 'Price → Profit'}
                </Button>
              </div>

              {mode === 'profit_to_price' ? (
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Target Profit ($)</label>
                  <Input type="number" step="0.01" value={targetProfit}
                    onChange={e => setTargetProfit(e.target.value)} placeholder="100"
                    data-testid="input-target-profit" />
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Your Purchase Price ($)</label>
                  <Input type="number" step="0.01" value={purchasePrice}
                    onChange={e => setPurchasePrice(e.target.value)} placeholder="400"
                    data-testid="input-purchase-price" />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Repair Costs ($)</label>
                <Input type="number" step="0.01" value={repairCosts}
                  onChange={e => setRepairCosts(e.target.value)} placeholder="0"
                  data-testid="input-repair-costs" />
              </div>
            </div>

            {/* Buyer Adjustments — deductions from the price sheet */}
            <div className="border-t border-border pt-4 space-y-3" data-testid="deductions-section">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">Buyer Adjustments</p>
                  <p className="text-xs text-muted-foreground">
                    {model ? 'Tap to include. Type just the number — it counts as a deduction.' : 'Pick a model first to see preset deductions.'}
                  </p>
                </div>
                {deductionState.length > 0 && (
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</p>
                    <p className="text-base font-bold text-destructive tabular-nums" data-testid="adjustments-total">
                      {adjustmentsTotal === 0 ? '$0' : `-$${Math.abs(adjustmentsTotal).toLocaleString()}`}
                    </p>
                  </div>
                )}
              </div>

              {model && deductionState.length === 0 && (
                <div className="rounded-md border border-dashed border-border px-3 py-3 text-center">
                  <AlertCircle className="h-4 w-4 inline mr-1.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    No presets parsed for {model}. Sync your KNO3 sheet or add custom deductions below.
                  </span>
                </div>
              )}

              {deductionState.length > 0 && (
                <div className="space-y-1.5">
                  {deductionState.map((d, idx) => (
                    <DeductionRow
                      key={d.__rid || `${d.source}-${idx}`}
                      d={d}
                      idx={idx}
                      onToggle={(e) => toggleDeduction(idx, e)}
                      onAmountChange={(v) => setDeductionAmount(idx, v)}
                      onLabelChange={(v) => updateCustomLabel(idx, v)}
                      onRemove={() => removeDeduction(idx)}
                    />
                  ))}
                </div>
              )}

              <Button
                type="button" variant="ghost" size="sm"
                onClick={addCustomDeduction}
                className="w-full gap-2 text-xs h-8 border border-dashed border-border"
                data-testid="add-custom-deduction"
              >
                <Plus className="h-3 w-3" /> Add custom deduction
              </Button>
            </div>

            <Button className="w-full" onClick={calculate}
              disabled={!allSelected || calculating || (mode === 'profit_to_price' ? targetProfit === '' : purchasePrice === '')} data-testid="calculate-btn">
              <Calculator className="h-4 w-4 mr-2" />
              {calculating ? 'Calculating...' : 'Calculate'}
            </Button>
          </Card>

          {/* Result */}
          {(!embedded || result) && <Card className={embedded ? 'p-3 rounded-2xl space-y-3' : 'p-6 space-y-4'} data-testid="calc-result">
            <h2 className="font-heading text-xl font-semibold text-foreground">Result</h2>
            {!result ? (
              <p className="text-sm text-muted-foreground">Pick a model + grade + storage + carrier and tap Calculate.</p>
            ) : result.mode === 'profit_to_price' ? (
              <div className="space-y-4">
                <div className="rounded-xl bg-primary text-primary-foreground px-6 py-5">
                  <p className="text-xs uppercase tracking-wide opacity-80">Max You Should Pay</p>
                  <p className="text-4xl font-bold mt-1" data-testid="result-max-price">
                    ${result.max_purchase_price.toLocaleString()}
                  </p>
                  <p className="text-xs mt-2 opacity-90">to lock in ${result.target_profit.toLocaleString()} profit</p>
                </div>
                <Breakdown result={result} deductions={deductionState.filter(d => d.enabled)} />
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`rounded-xl px-6 py-5 ${
                  result.expected_profit >= 0 ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-destructive/15 border border-destructive/30'
                }`}>
                  <p className="text-xs uppercase tracking-wide text-foreground/70">Expected Profit</p>
                  <p className={`text-4xl font-bold mt-1 ${
                    result.expected_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
                  }`} data-testid="result-profit">
                    {result.expected_profit >= 0 ? '+' : ''}${result.expected_profit.toLocaleString()}
                  </p>
                  <p className="text-xs mt-2 text-foreground/70 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Return on purchase cost: {result.margin_pct}%
                  </p>
                </div>
                <Breakdown result={result} deductions={deductionState.filter(d => d.enabled)} />
              </div>
            )}
          </Card>}
        </div>
      )}
    </div>
  );
}

function presetsToState(items) {
  return (items || []).map((it, i) => ({
    __rid: `p_${i}_${(it.label || '').replace(/\s+/g, '_').slice(0, 20)}`,
    label: it.label,
    // store amount as POSITIVE string for the input (sign is implicit/red)
    amount: it.amount != null ? String(Math.abs(it.amount)) : '',
    enabled: false,
    is_ask: !!it.is_ask,
    source: 'preset',
  }));
}

function DeductionRow({ d, idx, onToggle, onAmountChange, onLabelChange, onRemove }) {
  const isCustom = d.source === 'custom';
  const hasAmount = d.amount !== '' && !isNaN(parseFloat(d.amount));
  return (
    <div
      className={`flex items-center gap-2 rounded-md px-2 py-1.5 border ${
        d.enabled && hasAmount ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-background'
      } transition-colors`}
      data-testid={`deduction-row-${idx}`}
    >
      <Checkbox
        checked={d.enabled}
        onCheckedChange={(c) => onToggle(!!c)}
        disabled={!hasAmount && !isCustom}
        data-testid={`deduction-checkbox-${idx}`}
      />
      {isCustom ? (
        <Input
          value={d.label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="Label (e.g., No charger)"
          className="h-7 text-xs flex-1"
          data-testid={`deduction-label-${idx}`}
        />
      ) : (
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-foreground block truncate" title={d.label}>{d.label}</span>
          {d.is_ask && !hasAmount && (
            <span className="text-[10px] text-amber-600 font-medium">ASK — type your number</span>
          )}
        </div>
      )}
      <div className="relative w-24">
        <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${hasAmount && d.enabled ? 'text-destructive' : 'text-muted-foreground'} font-semibold`}>−$</span>
        <Input
          type="number"
          value={d.amount}
          onChange={(e) => onAmountChange(e.target.value)}
          placeholder={d.is_ask ? 'ASK' : '0'}
          className={`h-7 text-xs pl-7 tabular-nums ${hasAmount && d.enabled ? 'text-destructive font-bold border-destructive/40' : ''}`}
          data-testid={`deduction-amount-${idx}`}
        />
      </div>
      {isCustom && (
        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-deduction-${idx}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Breakdown({ result, deductions }) {
  const isMode1 = result.mode === 'profit_to_price';
  const active = (deductions || []).filter(d => d.amount !== '' && !isNaN(parseFloat(d.amount)));
  return (
    <div className="text-sm space-y-2 border-t border-border pt-4">
      <Row label="Buyer Price" value={`$${result.buyer_price.toLocaleString()}`} />
      {active.length > 0 && (
        <details className="text-xs" data-testid="adjustments-detail">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Adjustments: <span className="text-destructive font-semibold">-${Math.abs(result.adjustments).toLocaleString()}</span> ({active.length})
          </summary>
          <ul className="ml-3 mt-1 space-y-0.5">
            {active.map((d, i) => (
              <li key={i} className="flex justify-between text-destructive">
                <span>{d.label || 'Custom'}</span>
                <span className="tabular-nums">−${Math.abs(parseFloat(d.amount)).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {result.adjustments !== 0 && active.length === 0 && (
        <Row label="Adjustments" value={`${result.adjustments >= 0 ? '+' : ''}$${result.adjustments.toLocaleString()}`} />
      )}
      <Row label="Effective Buyer Price" value={`$${result.effective_buyer_price.toLocaleString()}`} bold />
      {result.repair_costs > 0 && (
        <Row label="− Repair Costs" value={`$${result.repair_costs.toLocaleString()}`} />
      )}
      {isMode1 ? (
        <>
          <Row label="− Target Profit" value={`$${result.target_profit.toLocaleString()}`} />
          <Row label="= Max Purchase Price" value={`$${result.max_purchase_price.toLocaleString()}`} bold accent />
        </>
      ) : (
        <>
          <Row label="− Purchase Price" value={`$${result.purchase_price.toLocaleString()}`} />
          <Row label="= Expected Profit" value={`$${result.expected_profit.toLocaleString()}`} bold accent />
        </>
      )}
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? 'font-semibold' : ''} ${accent ? 'text-primary' : 'text-foreground'}`}>
      <span className={accent ? 'text-primary' : 'text-muted-foreground'}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
